import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Modal,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
} from "react-native";
import { useNavigation } from "@react-navigation/native";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { Feather } from "@expo/vector-icons";
import { useSelector } from "react-redux";
import { LabelBannerWithSearchBar } from "../../../components/custom/Banners/LabelBannerWithSearchBar";
import { DataTableWithPagination } from "../../../components/custom/DataTable/DataTableWithPagination";
import DeviceOrientationContext from "../../../components/context/DeviceOrientationContext";
import { View } from "../../../components/shared/Themed";
import Spinner from "../../../components/shared/Spinner";
import { reserveStockModel } from "../../../models/reportsModel";
import { getReserveStocks } from "../../../services/stockService";
import { handleTableLoader } from "../../../utils/common";
import { filterBasedOnAnyKey } from "../../../utils/searchInArrays";
import { sortColumns } from "../../../utils/sortColumns";
import {
  headersReserveStock,
  mediumDeviceHeadersReserveStock,
  smallDeviceHeadersReserveStock,
  mobileDeviceHeadersReserveStock,
} from "../utils/reserveStockHeaders";
import { StockReportDisplaySwitch } from "../StocksReportScreens/utils/StockReportDisplaySwitch";

export default function ReserveStockReportScreen() {
  const navigation = useNavigation<any>();
  const { isLargeDevice, isMediumDevice, isMobileDevice, isSmallDevice } = useContext(
    DeviceOrientationContext
  );
  const columnsToNotHaveThousandSeparator: Array<string> = ["Order Number"];
  const { reserveStockReport, isReserveStockReportListLoaded } = useSelector(
    (state: any) => state.reports
  );

  const [reserveStockPageLevelList, setReserveStockPageLevelList] = useState<
    Array<reserveStockModel>
  >([]);
  const [isSearched, setIsSearched] = useState<boolean>(false);
  const [searchedText, setSearchedText] = useState<string>("");
  const [tableHeaders, setTableHeaders] = useState<
    Record<string, Record<string, string | number | boolean>>
  >(headersReserveStock);
  const [isRefresh, setRefresh] = useState<boolean>(false);
  const [_isDisplayViewOn, _setIsDisplayViewOn] = useState<boolean>(false);
  const [columnSettingsOpen, setColumnSettingsOpen] = useState<boolean>(false);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [settingsBtnPos, setSettingsBtnPos] = useState({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  });
  const settingsBtnRef = useRef<any>(null);

  useEffect(() => {
    AsyncStorage.getItem("reserve_stock_report_hidden_columns").then(
      (stored) => {
        if (stored) {
          try {
            setHiddenColumns(JSON.parse(stored));
          } catch { }
        }
      }
    );
  }, []);

  const getAllHeaders = () => {
    return isLargeDevice
      ? headersReserveStock
      : isMediumDevice
        ? mediumDeviceHeadersReserveStock
        : isMobileDevice
          ? mobileDeviceHeadersReserveStock
          : smallDeviceHeadersReserveStock;
  };

  const getFilteredHeaders = (
    baseHeaders: Record<string, Record<string, string | number | boolean>>
  ) => {
    const visibleEntries = Object.entries(baseHeaders).filter(
      ([key]) => !hiddenColumns.includes(key)
    );
    const totalOriginalFlex = Object.values(baseHeaders).reduce(
      (sum, header) => sum + (Number(header.width) || 0),
      0
    );
    const totalVisibleFlex = visibleEntries.reduce(
      (sum, [, header]) => sum + (Number(header.width) || 0),
      0
    );
    const scale = totalVisibleFlex > 0 ? totalOriginalFlex / totalVisibleFlex : 1;

    const result: Record<string, Record<string, string | number | boolean>> = {};
    visibleEntries.forEach(([key, value]) => {
      result[key] = { ...value, width: Number(value.width) * scale };
    });
    return result;
  };

  useEffect(() => {
    const allHeaders = getAllHeaders();
    setTableHeaders(getFilteredHeaders(allHeaders));
  }, [hiddenColumns, isLargeDevice, isMediumDevice, isSmallDevice, isMobileDevice]);

  const toggleColumn = (columnKey: string) => {
    setHiddenColumns((prev) => {
      const updated = prev.includes(columnKey)
        ? prev.filter((key) => key !== columnKey)
        : [...prev, columnKey];
      AsyncStorage.setItem(
        "reserve_stock_report_hidden_columns",
        JSON.stringify(updated)
      );
      return updated;
    });
  };

  const displayTableLoader = () => {
    handleTableLoader(true, false, setIsSearched);
  };

  const fadeTableLoader = () => {
    handleTableLoader(false, true, setIsSearched);
  };

  const displayReserveStockByCarton = (
    reserveStocks: Array<reserveStockModel>
  ) => {
    return reserveStocks.map((reserveStock: reserveStockModel) => ({
      ...reserveStock,
      "Open Qty PCS":
        Number(reserveStock["Open Qty PCS"]) /
        (Number(reserveStock.PerCtns) || 1),
    }));
  };

  const getDisplayedReserveStocks = (
    reserveStocks: Array<reserveStockModel>
  ) => {
    return _isDisplayViewOn
      ? displayReserveStockByCarton(reserveStocks)
      : reserveStocks;
  };

  const onHandleSortColumn = (columnKey: string) => {
    const sortedList: Array<reserveStockModel> = sortColumns(
      reserveStockPageLevelList,
      columnKey,
      tableHeaders,
      setTableHeaders
    );

    setReserveStockPageLevelList(sortedList);
  };

  const handleSearch = (searchText: string) => {
    displayTableLoader();
    setSearchedText(searchText);

    if (searchText.trim() !== "") {
      const searchedList: Array<reserveStockModel> = filterBasedOnAnyKey(
        reserveStockReport,
        searchText
      );
      setReserveStockPageLevelList(getDisplayedReserveStocks(searchedList));
    } else {
      setReserveStockPageLevelList(
        getDisplayedReserveStocks(reserveStockReport)
      );
    }

    setTimeout(() => {
      fadeTableLoader();
    }, 500);
  };

  useEffect(() => {
    let isMounted = true;

    if (isMounted && (reserveStockPageLevelList.length === 0 || isRefresh)) {
      displayTableLoader();
      getReserveStocks(tableHeaders);
    }

    const allHeaders = getAllHeaders();
    setTableHeaders(getFilteredHeaders(allHeaders));
    setRefresh(false);

    return () => {
      isMounted = false;
    };
  }, [isRefresh]);

  useEffect(() => {
    const filteredReserveStocks =
      searchedText.trim() !== ""
        ? filterBasedOnAnyKey(reserveStockReport, searchedText)
        : reserveStockReport;

    setReserveStockPageLevelList(
      getDisplayedReserveStocks(filteredReserveStocks)
    );

    setTimeout(() => {
      fadeTableLoader();
    }, 1000);
  }, [reserveStockReport, _isDisplayViewOn]);

  const navigateToReportDetails = (order: any, action: string) => {
    navigation.navigate("ReserveStockDetailsScreen", { order });
  };

  return (
    <ScrollView style={{ backgroundColor: "white" }}>
      <View style={[styles.container, { backgroundColor: "#f2f2f2" }]}>
        <View style={[styles.containerContent, { width: "100%" }]}>
          {isReserveStockReportListLoaded ? (
            <View>
              <LabelBannerWithSearchBar
                titleName={`Total Items: ${reserveStockPageLevelList.length}`}
                handleSearch={handleSearch}
                setRefresh={setRefresh}

                //Export Data to Excel
                dataToExport={reserveStockPageLevelList.map((reserveStock) => {
                  const pieces = _isDisplayViewOn
                    ? Number(reserveStock["Open Qty PCS"]) * (Number(reserveStock.PerCtns) || 1)
                    : Number(reserveStock["Open Qty PCS"]);
                  const cartons = _isDisplayViewOn
                    ? Number(reserveStock["Open Qty PCS"])
                    : Number(reserveStock["Open Qty PCS"]) / (Number(reserveStock.PerCtns) || 1);

                  const formatToDDMMYYYY = (dStr: any) => {
                    if (!dStr) return "";
                    const date = new Date(dStr);
                    if (isNaN(date.getTime())) return String(dStr).split(" ")[0];
                    return `${String(date.getDate()).padStart(2, "0")}/${String(date.getMonth() + 1).padStart(2, "0")}/${date.getFullYear()}`;
                  };

                  const formatNumber = (val: any) => {
                    if (val === null || val === undefined || val === "") return val;
                    const num = Number(val);
                    return isNaN(num) ? val : num.toLocaleString("en-US", { maximumFractionDigits: 2 });
                  };

                  return {
                    "Order Number": reserveStock.DocNum,
                    "Customer Code": reserveStock.CardCode,
                    "Customer Name": reserveStock.CardName,
                    "Address": reserveStock.Address2,
                    "Warehouse": reserveStock.WhsName,
                    "Item Code": reserveStock.ItemCode,
                    "Description": reserveStock.Dscription,
                    "Order Reserved (Pieces)": formatNumber(pieces),
                    "Order Reserved (Cartons)": formatNumber(cartons),
                    "Sales": reserveStock.SlpName,
                    "Order Date": formatToDDMMYYYY(reserveStock["Order Date"]),
                    "Valid until": formatToDDMMYYYY(reserveStock["Valid until"]),
                    "Doc Status": reserveStock["Doc Status"],
                  };
                })}
                fileName="reserveStockReport"
                sheetName="Reserve Stock Report" 
                
                extraControls={(
                  <View style={{ flexDirection: "row", alignItems: "center" }}>
                    <StockReportDisplaySwitch
                      title="Display"
                      isDisplayViewOn={_isDisplayViewOn}
                      setIsDisplayViewOn={_setIsDisplayViewOn}
                    />
                    {!isMobileDevice && (
                      <TouchableOpacity
                        ref={settingsBtnRef}
                        style={styles.settingsButton}
                        onPress={() => {
                          if (settingsBtnRef.current) {
                            settingsBtnRef.current.measureInWindow(
                              (
                                x: number,
                                y: number,
                                width: number,
                                height: number
                              ) => {
                                setSettingsBtnPos({
                                  x: x || 0,
                                  y: y || 0,
                                  width: width || 0,
                                  height: height || 0,
                                });
                                setColumnSettingsOpen(!columnSettingsOpen);
                              }
                            );
                          } else {
                            setColumnSettingsOpen(!columnSettingsOpen);
                          }
                        }}
                      >
                        <Feather
                          name="settings"
                          size={15}
                          color={columnSettingsOpen ? "#56B7FD" : "#6B7280"}
                        />
                      </TouchableOpacity>
                    )}
                  </View>
                )}
              />
              <DataTableWithPagination
                headers={tableHeaders}
                rows={reserveStockPageLevelList}
                itemsPerPage={100}
                useModal={false}
                isDetail={isMobileDevice}
                setModalVisible={() => { }}
                setSelected={() => { }}
                isDataLoading={isSearched}
                columnsToNotHaveThousandSeparator={
                  columnsToNotHaveThousandSeparator
                }
                handleRowClick={() => { }}
                handleDeleteRow={() => { }}
                handleSortColumn={onHandleSortColumn}
                selectable={true}
                operationActions={navigateToReportDetails}
              />
              {columnSettingsOpen && (
                <Modal
                  transparent={true}
                  visible={columnSettingsOpen}
                  animationType="fade"
                  onRequestClose={() => setColumnSettingsOpen(false)}
                >
                  <Pressable
                    style={styles.overlay}
                    onPress={() => setColumnSettingsOpen(false)}
                  >
                    <Pressable
                      style={[
                        styles.menu,
                        {
                          top: settingsBtnPos.y + settingsBtnPos.height + 6,
                          left: settingsBtnPos.x - 180 + 40,
                        },
                      ]}
                      onPress={(event) => event.stopPropagation()}
                    >
                      <Text style={styles.menuTitle}>Show / Hide Columns</Text>
                      <View style={styles.menuDivider} />
                      <TouchableOpacity
                        style={styles.menuRow}
                        onPress={() => {
                          const allKeys = Object.keys(getAllHeaders()).filter(
                            (key) => key !== ""
                          );
                          if (hiddenColumns.length === 0) {
                            const updated = [...allKeys];
                            setHiddenColumns(updated);
                            AsyncStorage.setItem(
                              "reserve_stock_report_hidden_columns",
                              JSON.stringify(updated)
                            );
                          } else {
                            setHiddenColumns([]);
                            AsyncStorage.setItem(
                              "reserve_stock_report_hidden_columns",
                              JSON.stringify([])
                            );
                          }
                        }}
                      >
                        <View
                          style={[
                            styles.checkbox,
                            hiddenColumns.length === 0 && styles.checkboxActive,
                          ]}
                        >
                          {hiddenColumns.length === 0 && (
                            <Feather name="check" size={13} color="#fff" />
                          )}
                        </View>
                        <Text style={styles.menuRowLabel}>Select All</Text>
                      </TouchableOpacity>
                      <View style={styles.menuDivider} />
                      <ScrollView
                        style={{ maxHeight: 300 }}
                        showsVerticalScrollIndicator={true}
                      >
                        {Object.entries(getAllHeaders()).map(([key, value]) => {
                          if (key === "") return null;
                          const isVisible = !hiddenColumns.includes(key);
                          return (
                            <TouchableOpacity
                              key={key}
                              style={styles.menuRow}
                              onPress={() => toggleColumn(key)}
                            >
                              <View
                                style={[
                                  styles.checkbox,
                                  isVisible && styles.checkboxActive,
                                ]}
                              >
                                {isVisible && (
                                  <Feather name="check" size={13} color="#fff" />
                                )}
                              </View>
                              <Text
                                style={[
                                  styles.columnLabel,
                                  !isVisible && styles.columnLabelHidden,
                                ]}
                              >
                                {(value as any).name}
                              </Text>
                            </TouchableOpacity>
                          );
                        })}
                      </ScrollView>
                    </Pressable>
                  </Pressable>
                </Modal>
              )}
            </View>
          ) : (
            <View>
              <Spinner
                size="small"
                color="#74BBFF"
                styles={{ flexDirection: "row", margin: "20%" }}
              />
            </View>
          )}
        </View>
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    height: "100%",
  },
  containerContent: {
    flexDirection: "column",
    alignSelf: "flex-start",
  },
  settingsButton: {
    width: 40,
    height: 40,
    borderRadius: 10,
    backgroundColor: "#F3F4F6",
    justifyContent: "center",
    alignItems: "center",
    borderWidth: 1,
    borderColor: "#E2E8F0",
  },
  overlay: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.1)",
  },
  menu: {
    position: "absolute",
    backgroundColor: "#fff",
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#E2E8F0",
    paddingVertical: 10,
    minWidth: 220,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.15,
    shadowRadius: 20,
    elevation: 10,
  },
  menuTitle: {
    fontSize: 12,
    fontWeight: "700",
    color: "#6B7280",
    paddingHorizontal: 16,
    paddingBottom: 8,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  menuDivider: {
    height: 1,
    backgroundColor: "#F1F5F9",
    marginBottom: 4,
  },
  menuRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 8,
    paddingHorizontal: 16,
  },
  menuRowLabel: {
    fontSize: 14,
    color: "#111827",
    fontWeight: "700",
  },
  checkbox: {
    width: 20,
    height: 20,
    borderRadius: 4,
    borderWidth: 1.5,
    borderColor: "#D1D5DB",
    backgroundColor: "#fff",
    justifyContent: "center",
    alignItems: "center",
    marginRight: 10,
  },
  checkboxActive: {
    borderColor: "#56B7FD",
    backgroundColor: "#56B7FD",
  },
  columnLabel: {
    fontSize: 14,
    color: "#111827",
    fontWeight: "600",
  },
  columnLabelHidden: {
    color: "#9CA3AF",
    fontWeight: "400",
  },
});